import type { AxiosInstance } from 'axios';
import { unwrap, type ObjectResponse } from '../core/envelope';

// Re-export the shared envelope shape so any external consumer importing
// `ObjectResponse` from this file keeps working unchanged.
export type { ObjectResponse } from '../core/envelope';

/**
 * Multi-tenant login payload — mirrors the live `LoginRequest` schema at
 * `https://api.knottique.kristysoftware.com/swagger/v1/swagger.json`.
 *
 * - `tenantCode` (optional, max 60) — explicit tenant short code.
 * - `userName` (required, max 80) — credential username (camelCase per spec).
 * - `password` (required, max 128).
 *
 * The backend resolves the tenant in this priority: `tenantCode` →
 * domain → subdomain → host lookup via `TenantDomainMap`.
 */
export interface LoginPayload {
  tenantCode?: string;
  userName: string;
  password: string;
}

/**
 * Refresh-token rotation payload. The live spec requires **both** the
 * refresh token and the session id — sessions are tracked server-side and
 * the pair must match for reuse-detection to work.
 */
export interface RefreshPayload {
  refreshToken: string;
  sessionId: string;
}

/** Logout / revoke. The backend invalidates by session id, not token. */
export interface RevokeRequest {
  sessionId: string;
}

export interface RegisterPayload {
  tenantCode?: string;
  storeId?: string;
  userName: string;
  email: string;
  phone?: string;
  password: string;
  firstName: string;
  lastName?: string;
}

export interface ForgotPasswordPayload {
  tenantCode?: string;
  email: string;
}

export interface ResetPasswordPayload {
  token: string;
  newPassword: string;
  confirmPassword?: string;
}

/**
 * Profile fields the frontend needs after login. The live API does **not**
 * include these in the login response body — they live in the JWT claims.
 * Use `decodeJwt(accessToken)` from `@kss/auth` to populate this shape.
 */
export interface LoginUserProfile {
  id: string;
  name: string;
  email: string;
  role: string;
  avatar?: string;
  permissions?: string[];
}

/**
 * Flat login response payload — exact shape returned by
 * `POST /api/auth/Login` (verified live 2026-04-28).
 *
 * NOTE: there is **no** nested `user` object. Display name / email / role /
 * permission codes are JWT claims and must be decoded client-side.
 */
export interface AuthTokenBundle {
  accessToken: string;
  refreshToken: string;
  /** Absolute ISO datetime when accessToken expires (~15 min after issue). */
  expiresAt: string;
  tenantId: string;
  sessionId: string;
  userId: string;
  userName: string;
}

/** Alias — login and refresh both resolve the same flat envelope payload. */
export type LoginResponse = AuthTokenBundle;

export function createAuthService(client: AxiosInstance) {
  return {
    /** `POST /api/auth/Login` */
    login: (payload: LoginPayload) =>
      client
        .post<ObjectResponse<LoginResponse>>('/auth/Login', payload)
        .then((r) => unwrap(r.data)),

    /** `POST /api/auth/refresh` — rotates the refresh-token chain. */
    refresh: (payload: RefreshPayload) =>
      client
        .post<ObjectResponse<AuthTokenBundle>>('/auth/refresh', payload)
        .then((r) => unwrap(r.data)),

    /** `POST /api/auth/revoke` — server-side logout / session invalidation. */
    revoke: (payload: RevokeRequest) =>
      client.post<ObjectResponse<unknown>>('/auth/revoke', payload).then((r) => r.data),

    /**
     * `POST /api/auth/revoke` — alias kept for the legacy `logout()` callsite
     * in `useSignOut`. Pass the active sessionId via `payload.sessionId`.
     */
    logout: (payload?: RevokeRequest) =>
      payload?.sessionId
        ? client.post<ObjectResponse<unknown>>('/auth/revoke', payload).then(() => undefined)
        : Promise.resolve(undefined),

    /**
     * `GET /api/auth/session/{sessionId}` — confirms the live session is
     * still active server-side. Returns the session metadata; the actual
     * user identity must be decoded from the JWT (no profile fields here).
     */
    session: (sessionId: string) =>
      client
        .get<ObjectResponse<{
          sessionId: string;
          active: boolean;
          info?: {
            sessionId: string;
            userId: string;
            tenantId: string;
            userName: string;
            refreshToken?: string;
            issuedAt?: string;
            absoluteExpiresAt?: string;
            refreshExpiresAt?: string;
            lastUsedAt?: string;
            isRevoked?: boolean;
            revokedAt?: string | null;
            ip?: string;
            userAgent?: string;
          };
        }>>(`/auth/session/${encodeURIComponent(sessionId)}`)
        .then((r) => unwrap(r.data)),

    /** `POST /api/auth/register` */
    register: (payload: RegisterPayload) =>
      client.post<ObjectResponse<LoginResponse>>('/auth/register', payload).then((r) => unwrap(r.data)),

    /**
     * `POST /api/auth/me/orders` — customer-scoped order feed
     * (verified live 2026-05-05). Always filters strictly to the
     * `customerId` claim in the JWT, so tenant-scope leaks aren't
     * possible. Returns paged items.
     */
    myOrders: (payload?: { page?: number; pageSize?: number }) =>
      client
        .post<
          ObjectResponse<{
            items: unknown[];
            total: number;
            page: number;
            pageSize: number;
            totalPages: number;
          }>
        >('/auth/me/orders', payload ?? {})
        .then((r) => unwrap(r.data)),

    /** `POST /api/auth/forgot-password` */
    forgotPassword: (payload: ForgotPasswordPayload) =>
      client.post<ObjectResponse<unknown>>('/auth/forgot-password', payload).then((r) => r.data),

    /** `POST /api/auth/reset-password` */
    resetPassword: (payload: ResetPasswordPayload) =>
      client.post<ObjectResponse<unknown>>('/auth/reset-password', payload).then((r) => r.data),

    /** `POST /api/auth/verify-email` */
    verifyEmail: (token: string) =>
      client.post<ObjectResponse<unknown>>('/auth/verify-email', { token }).then((r) => r.data),

    /** `POST /api/auth/send-verification` */
    sendVerification: (email: string, tenantCode?: string) =>
      client
        .post<ObjectResponse<unknown>>('/auth/send-verification', { email, tenantCode })
        .then((r) => r.data),

    /**
     * Alias kept for backwards-compatibility with the older
     * `authService.me()` callsite. Looks up the active session by id.
     */
    me: () => Promise.reject<LoginUserProfile>(new Error('Use authService.session(sessionId) instead.')),

    /* ------------------------------------------------------------- */
    /* Customer self-service endpoints (BACKEND_AGENT_PROMPTS Item 6) */
    /* Each is JWT-customerId-scoped — never accept customerId in a   */
    /* request body. Until the live endpoints ship, every call throws */
    /* via decorateError; the calling page falls back to localStorage. */
    /* ------------------------------------------------------------- */

    /** `POST /api/auth/me/profile` — fetch the signed-in profile row. */
    getMyProfile: () =>
      client
        .post<ObjectResponse<{
          id?: string;
          name?: string;
          email?: string;
          phone?: string;
          imageUrl?: string;
          createdAt?: string;
        }>>('/auth/me/profile', {})
        .then((r) => unwrap(r.data)),

    /** `PUT /api/auth/me/profile { name?, email?, phone? }` — update. */
    updateMyProfile: (patch: { name?: string; email?: string; phone?: string; imageUrl?: string }) =>
      client
        .put<ObjectResponse<{
          id?: string;
          name?: string;
          email?: string;
          phone?: string;
          imageUrl?: string;
        }>>('/auth/me/profile', patch)
        .then((r) => unwrap(r.data)),

    /** `POST /api/auth/me/preferences` — list notification toggles. */
    getMyPreferences: () =>
      client
        .post<ObjectResponse<{ items?: Array<{ prefKey: string; enabled: boolean }> } | Array<{ prefKey: string; enabled: boolean }>>>(
          '/auth/me/preferences',
          {},
        )
        .then((r) => {
          const d = r.data?.data;
          if (Array.isArray(d)) return d;
          return d?.items ?? [];
        }),

    /** `PUT /api/auth/me/preferences { items }` — bulk upsert prefs. */
    saveMyPreferences: (items: Array<{ prefKey: string; enabled: boolean }>) =>
      client
        .put<ObjectResponse<unknown>>('/auth/me/preferences', { items })
        .then((r) => r.data),

    /**
     * `POST /api/auth/change-password { oldPassword, newPassword }`
     * — staff-side password rotation. Body field names verified
     * against the live API (2026-05-14): backend uses `oldPassword`
     * (NOT `currentPassword`) plus `newPassword`. Both 6–128 chars.
     * Verifies the current hash and rate-limits failures. Does NOT
     * rotate the active session.
     */
    changePassword: (body: { currentPassword: string; newPassword: string }) =>
      client
        .post<ObjectResponse<{ success: boolean }>>('/auth/change-password', {
          oldPassword: body.currentPassword,
          newPassword: body.newPassword,
        })
        .then((r) => unwrap(r.data)),
  };
}

export type AuthService = ReturnType<typeof createAuthService>;
